import { createFileRoute, notFound, Outlet, useLocation } from '@tanstack/react-router'

import { usePapeis } from '@/lib/sessao'
import {
  buscarPerfil,
  DETALHES,
  ehPerfilId,
  lerEnderecoDaVisao,
  paginasDoPerfil,
  paginasVisiveis,
  type Perfil,
  perfilDaPessoa,
  perfisComPagina,
} from '@/lib/visoes'

import { Casca } from '@/components/layout/casca'
import { Redirecionar } from '@/components/layout/redirecionar'

import { PaginaNaoEncontrada, SemPermissao } from '@/features/acesso/tela-aviso'

export const Route = createFileRoute('/$perfil')({
  beforeLoad: ({ params }) => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- é assim que o TanStack Router pede
    if (!ehPerfilId(params.perfil)) throw notFound()
  },
  component: PainelDaVisao,
})

/**
 * Mesmo endereço na visão da pessoa, se ela tem a página (ex.: um gestor que
 * abre `/time/meus-alertas` vai para `/gestao/meus-alertas`).
 */
function naVisaoDaPessoa(
  perfilAtual: Perfil,
  endereco: ReturnType<typeof lerEnderecoDaVisao>,
  papeis: ReadonlySet<string>,
  resto: string
): string | null {
  const propria = perfilDaPessoa(papeis)
  if (propria.id === perfilAtual.id) return null
  const temPagina = paginasVisiveis(propria, papeis).some((p) => p.id === endereco.paginaDoMenu)
  const temDetalhe = !endereco.detalhe || endereco.detalhe.perfis.includes(propria.id)
  return temPagina && temDetalhe ? `/${propria.id}${resto}` : null
}

/**
 * Casca das páginas novas (`/<visão>/...`): confere se a visão é a da pessoa e
 * se ela abre a página, e desenha o menu da visão.
 */
function PainelDaVisao() {
  // A visão sai do endereço (e não dos parâmetros da rota): durante a troca de
  // página os dois podem ficar diferentes por um instante.
  const { pathname, searchStr } = useLocation()
  const perfilId = pathname.split('/')[1]
  const papeis = usePapeis()
  const perfil = buscarPerfil(perfilId)
  const liberada = perfil ? perfil.id === perfilDaPessoa(papeis).id : false

  const enderecoCompleto = `${pathname}${searchStr}`
  if (!perfil) return <PaginaNaoEncontrada endereco={enderecoCompleto} />

  const endereco = lerEnderecoDaVisao(pathname)
  // `/gestao` sozinho: a primeira página da visão (feito pela rota index).
  if (!endereco.segmento) return <Outlet />
  // `/gestao/times` sem o time: volta para a Visão geral.
  const detalheSemId = endereco.id ? undefined : DETALHES[endereco.segmento]
  if (detalheSemId && detalheSemId.paginaDoMenu !== endereco.segmento) {
    return <Redirecionar para={`/${perfil.id}/${detalheSemId.paginaDoMenu}`} />
  }
  if (endereco.invalido) return <PaginaNaoEncontrada endereco={enderecoCompleto} />

  const pagina = paginasDoPerfil(perfil).find((p) => p.id === endereco.paginaDoMenu)
  const detalheNaVisao = !endereco.detalhe || endereco.detalhe.perfis.includes(perfil.id)
  const resto = `${pathname.slice(perfil.id.length + 1)}${searchStr}`

  if (!pagina || !detalheNaVisao) {
    // A página existe na visão da pessoa: vai para lá.
    const outra = naVisaoDaPessoa(perfil, endereco, papeis, resto)
    if (outra) return <Redirecionar para={outra} />
    const existe = perfisComPagina(endereco.paginaDoMenu).length > 0
    return existe ? (
      <SemPermissao endereco={enderecoCompleto} />
    ) : (
      <PaginaNaoEncontrada endereco={enderecoCompleto} />
    )
  }

  if (!liberada || !paginasVisiveis(perfil, papeis).some((p) => p.id === pagina.id)) {
    const outra = naVisaoDaPessoa(perfil, endereco, papeis, resto)
    if (outra) return <Redirecionar para={outra} />
    return <SemPermissao endereco={enderecoCompleto} visaoPedida={perfil.nome} />
  }

  return (
    <Casca perfil={perfil} paginaAtiva={pagina.id}>
      <Outlet />
    </Casca>
  )
}
